import { FileText } from "lucide-react";
import { useSelector } from "react-redux";

import { OfferFacts } from "@/components/application/merchant-tasks/config/OfferTerms";
import OfferCalculator from "@/components/underwriting/OfferCalculator";

import type { ToolArtifact } from "../../aui/convert";
import type { WireChoice } from "../../protocol/protocol";
import type { AgentState } from "../../store/agentSlice";
import { selectChoice } from "../../store/selectors";
import type { NativeCard, NativeCardProps } from "..";
import {
  CardFooter,
  CardShell,
  CHANGE_HINT,
  ChangeHint,
  EDIT_HINT,
  ReceiptLine,
  StripSection,
  TermChanges,
} from "./CardParts";
import { ChoiceButtons, GenericApproval } from "./ChoiceParts";
import { useOfferEdits, type OfferEdit } from "./overrides";
import {
  APPROVAL_CARD_VERSION,
  calculatorTerms,
  isOfferApprovalCard,
  isOfferPayload,
  money,
  num,
  termChanges,
  type AnalysisStrip,
  type OfferApprovalCard,
  type OfferCosts,
  type OfferTerms as Terms,
} from "./payloads";

/** What the card draws: the offer get_offer read or the save stored, or the one a save proposes. */
interface OfferView {
  applicationId: number;
  /** The application's status, as the header's pill. */
  status: string | null;
  terms: Terms | null;
  costs: OfferCosts | null;
  strip: AnalysisStrip | null;
  /** Keys the Offer Builder to these terms. */
  version: string;
}

/**
 * The Offer Builder at the offer's terms, read-only or the user's to change (`edit`), then the
 * offer's facts under it (as the card shows them: an edit's are the redrawn card's).
 */
function OfferTerms({
  view,
  calculatorKey,
  edit,
}: {
  view: OfferView;
  calculatorKey: string;
  edit?: OfferEdit;
}) {
  const { terms, costs } = view;
  if (!terms) return <p className="text-sm text-muted-foreground">No offer yet.</p>;
  return (
    <div className="space-y-3">
      <OfferCalculator
        key={`${calculatorKey}:${edit?.key ?? ""}`}
        inline
        readOnly={!edit}
        persistDraft={false}
        currentLeveragePct={view.strip?.leverage?.percent ?? 0}
        averageRevenue={view.strip?.average_revenue ?? 0}
        initialValues={calculatorTerms(terms)}
        onOfferChange={edit?.onOffer}
        onLastEditedChange={edit?.onLastEdited}
      />
      <OfferFacts
        layout="auto"
        // Funding, payment and frequency are the Offer Builder's above.
        facts={{
          fundingAmount: "",
          payback: money(costs?.payback ?? null),
          disbursed: money(costs?.disbursed ?? null),
          specifiedPercentage: "",
          payment: "",
          frequency: "",
          originationFee: money(costs?.origination_fee ?? null),
        }}
        origination={num(terms.origination_fee_percentage) ?? 0}
      />
    </div>
  );
}

function Offer({
  view,
  approval,
  choice,
  artifact,
}: {
  view: OfferView;
  /** The write's approval card: what it changes. */
  approval: OfferApprovalCard | null;
  choice: WireChoice | null;
  artifact: ToolArtifact;
}) {
  const pending = choice?.status === "pending";
  const write = choice !== null;
  const { offerEdit, edits } = useOfferEdits(approval ? choice : null);
  return (
    <CardShell
      icon={FileText}
      title={write ? "Update offer terms" : "Offer"}
      status={view.status}
      data-state={pending ? "pending" : artifact.state}
    >
      <StripSection strip={view.strip} />
      <div className="border-b px-3.5 py-3">
        <OfferTerms
          view={view}
          calculatorKey={`${choice?.ref ?? "read"}:${view.version}`}
          edit={pending ? offerEdit : undefined}
        />
        {approval && <TermChanges changes={termChanges(approval)} />}
        {pending && <ChangeHint>{offerEdit ? EDIT_HINT : CHANGE_HINT}</ChangeHint>}
      </div>
      <CardFooter applicationId={view.applicationId}>
        {pending ? (
          // A press that redraws the card says so beside these buttons.
          <ChoiceButtons choice={choice} edits={edits} />
        ) : artifact.state === "failed" ? (
          <p className="min-w-0 text-xs font-medium text-destructive">{artifact.line}</p>
        ) : write ? (
          <ReceiptLine choice={choice} done="Saved" notDone="Not saved" />
        ) : null}
      </CardFooter>
    </CardShell>
  );
}

/**
 * get_offer: the strip, the Offer Builder read-only and the offer's facts. update_offer_terms:
 * the same at the terms it would save (its approval card), with what it changes and Save terms /
 * Don't save, then its receipt.
 */
function OfferCard({ result, artifact }: NativeCardProps) {
  const choice = useSelector((state: { agent: AgentState }) =>
    artifact.choiceRef ? selectChoice(state, artifact.choiceRef) : null
  );
  const approval = isOfferApprovalCard(choice?.card) ? choice.card : null;
  let view: OfferView | null = null;
  if (artifact.state === "success" && isOfferPayload(result)) {
    view = {
      applicationId: result.application_id,
      status: result.app_status,
      terms: result.terms,
      costs: result.costs,
      strip: result.strip,
      version: result.updated_at ?? "",
    };
  } else if (approval) {
    view = {
      applicationId: approval.application.id,
      // Until it is saved, the status it is in.
      status: approval.status.from,
      terms: approval.terms,
      costs: approval.costs,
      strip: approval.strip,
      version: approval.offer_sha256,
    };
  }
  if (!view) return <GenericApproval artifact={artifact} />;
  return <Offer view={view} approval={approval} choice={choice} artifact={artifact} />;
}

export const OFFER_CARD: NativeCard = {
  Card: OfferCard,
  // A write with no approval card (an older message) or one of another version keeps its
  // generic card.
  shows: (artifact, result) =>
    (artifact.state === "success" && isOfferPayload(result)) ||
    (artifact.choiceCardKind === "offer" && artifact.choiceCardVersion === APPROVAL_CARD_VERSION),
};
